import { useToast } from '@chakra-ui/react';
import { useCreatePDPMutation, useUpdatePDPMutation } from 'core/API/pdpApi';
import { useTranslation } from 'helpers/languages/translations';
import { useRef, useState } from 'react';
import { TOptionalProductDetails } from 'types';
import { TProductDetailsHooks } from '../../useProductDetails';
import { isPDPPermissionError } from 'pages/pdp/errors';

interface IProps {
    dataToSubmit: TProductDetailsHooks['dataToSubmit'];
    setDataToSubmit: TProductDetailsHooks['setDataToSubmit'];
    setIsModalOpen: TProductDetailsHooks['setIsModalOpen'];
    modalType: TProductDetailsHooks['modalType'];
    deletePDPs: TProductDetailsHooks['deletePDPs'];
    isDeleteLoading: TProductDetailsHooks['isDeleteLoading'];
    lang: TProductDetailsHooks['lang'];
    retailer: TProductDetailsHooks['retailer'];
    dateInWeeks: TProductDetailsHooks['dateInWeeks'];
}

const useModal = ({
    dataToSubmit,
    setDataToSubmit,
    setIsModalOpen,
    modalType,
    lang,
    retailer,
    deletePDPs,
    dateInWeeks,
}: IProps) => {
    const [addPDP, { isLoading: isAddPDPLoading }] = useCreatePDPMutation();

    const [updatePDP, { isLoading: isPDPUpdateLoading }] = useUpdatePDPMutation();

    const [searchValue, setSearchValue] = useState<string>('');
    const [toSubmitSearchValue, setToSubmitSearchValue] = useState<string>('');
    const translate = useTranslation();
    const updateInitialRef = useRef(null);
    const deleteInitialRef = useRef(null);
    const toast = useToast();

    const handleCloseModal = () => {
        setDataToSubmit({});
        setSearchValue('');
        setToSubmitSearchValue('');
        setIsModalOpen(false);
    };

    const handleInputChange = (value: string, field: string) => {
        setDataToSubmit((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async () => {
        try {
            const bulletPoints = dataToSubmit.bulletPoints?.filter((point) => point.trim());

            if (!dataToSubmit.nutriscore) delete dataToSubmit.nutriscore;
            const requestBody: TOptionalProductDetails = {
                ...dataToSubmit,
                retailer: retailer === 'default' ? undefined : retailer?.id,
                language: lang === 'default' ? undefined : lang,
            };

            if (bulletPoints) {
                requestBody.bulletPoints = bulletPoints;
            }
            if (!bulletPoints?.length) delete requestBody.bulletPoints;

            if (modalType === 'add') {
                await addPDP(requestBody).unwrap();
            } else {
                await updatePDP(requestBody).unwrap();
            }

            toast({
                title: translate('pdp.toast.success.update'),
                status: 'success',
                isClosable: true,
            });
            handleCloseModal();
        } catch (e) {
            toast({
                title: translate(isPDPPermissionError(e) ? 'pdp.error.permission_denied' : 'error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleDeletePDP = async () => {
        try {
            await deletePDPs([dataToSubmit._id as string]).unwrap();
            toast({
                title: translate('pdp.toast.success.delete'),
                status: 'success',
                isClosable: true,
            });
            handleCloseModal();
        } catch (e) {
            toast({
                title: translate(isPDPPermissionError(e) ? 'pdp.error.permission_denied' : 'error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    return {
        setIsModalOpen,
        isAddPDPLoading,
        handleCloseModal,
        handleSubmit,
        updateInitialRef,
        isPDPUpdateLoading,
        deleteInitialRef,
        handleDeletePDP,
        handleInputChange,
        searchValue,
        toSubmitSearchValue,
    };
};

export default useModal;
export type TModalHooks = ReturnType<typeof useModal>;
